import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Pencil } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, Field } from "@/components/shared";

const FIELDS = [["legal_name", "Legal name"], ["registered_address", "Registered address"], ["pan", "PAN"], ["gstin", "GSTIN"], ["cin", "CIN"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"], ["website", "Website"]];

export const copyText = async (v, label) => { try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); } catch { toast.error("Copy not available in this browser"); } };

export const CopyBtn = ({ value, label, testid }) => value ? <button type="button" className="text-slate-400 hover:text-teal-700 inline-flex" onClick={() => copyText(value, label)} title={`Copy ${label}`} data-testid={testid}><Copy className="h-3.5 w-3.5" /></button> : null;

const saveBlob = (data, name) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); };

export const CompanyDetails = ({ canEdit, canReveal }) => {
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/onboarding/company").then((r) => setC(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.put("/onboarding/company", f); setC(data); setEdit(false); toast.success("Company details saved"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const sheet = async (bank) => {
    try { const res = await api.get(`/onboarding/details-sheet?include_bank=${bank ? 1 : 0}`, { responseType: "blob" }); saveBlob(res.data, `MarksMan-Company-${bank ? "Bank-" : ""}Details.pdf`); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!c) return null;
  return (
    <Card title="Company details" testid="company-details-card" action={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sheet(false)} data-testid="download-sheet-button"><Download className="h-3.5 w-3.5 mr-1" /> Details sheet (PDF)</Button>
        {canReveal && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sheet(true)} data-testid="download-sheet-bank-button"><Download className="h-3.5 w-3.5 mr-1" /> With bank details</Button>}
        {canEdit && !edit && <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700" onClick={() => { setF(c); setEdit(true); }} data-testid="edit-company-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
      </div>}>
      {edit ? (
        <form onSubmit={save} className="grid grid-cols-2 gap-3" data-testid="company-form">
          {FIELDS.map(([k, l]) => (
            <Field key={k} label={l}>{k === "registered_address" ? <Textarea data-testid={`company-${k}`} value={f[k] || ""} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} rows={2} /> : <Input data-testid={`company-${k}`} value={f[k] || ""} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} className="h-9" />}</Field>
          ))}
          <div className="col-span-2 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setEdit(false)}>Cancel</Button><Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy} data-testid="company-save-button">{busy ? "Saving…" : "Save"}</Button></div>
        </form>
      ) : (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "registered_address" ? "col-span-2" : ""}><dt className="text-xs text-slate-500">{l}</dt><dd className="text-sm text-slate-800 flex items-start gap-2 whitespace-pre-line" data-testid={`company-view-${k}`}>{c[k] || <span className="italic text-slate-400">Not entered</span>}<CopyBtn value={c[k]} label={l} testid={`copy-company-${k}`} /></dd></div>
          ))}
          {c.updated_at && <div className="col-span-2 text-[11px] text-slate-400">Last updated {new Date(c.updated_at).toLocaleString()} by {c.updated_by}</div>}
        </dl>
      )}
    </Card>
  );
};
